import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, Search, Plus, Edit2, Trash2, Phone, Mail, MapPin,
  CreditCard, Truck, Clock, CheckCircle, AlertTriangle, XCircle,
  ChevronDown, ChevronUp, ExternalLink, FileText, DollarSign, Users,
  Eye, BarChart3, CloudUpload, Loader2, Download, RefreshCw, MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/context/SettingsContext';
import { useAuth } from '@/context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VENDOR_CATEGORIES = [
  "Furniture Manufacturer",
  "Raw Materials",
  "Packaging",
  "Hardware & Fittings",
  "Fabric & Upholstery",
  "Glass & Mirror",
  "Lighting",
  "Decor Items",
  "Logistics",
  "Other"
];

const PAYMENT_TERMS = [
  "Advance",
  "COD",
  "Net 7",
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60"
];

const VendorMasterPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedVendors, setExpandedVendors] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Check if user is admin or has specific permissions
  const isAdmin = user?.is_admin || user?.roles?.includes('admin');
  const canCreateVendor = isAdmin || userPermissions?.includes('vendors.create');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Zoho sync state
  const [syncingVendor, setSyncingVendor] = useState(null);
  const [zohoStatus, setZohoStatus] = useState(null);
  
  // Zoho Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [agingOpen, setAgingOpen] = useState(false);
  const [agingData, setAgingData] = useState(null);
  const [agingLoading, setAgingLoading] = useState(false);

  const fetchAging = async () => {
    setAgingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/enterprise/vendors/payables-aging`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgingData(res.data);
    } catch (e) {
      toast.error('Failed to load aging report');
    } finally {
      setAgingLoading(false);
    }
  };
  const [zohoVendors, setZohoVendors] = useState([]);
  const [loadingZohoVendors, setLoadingZohoVendors] = useState(false);
  const [selectedZohoVendors, setSelectedZohoVendors] = useState([]);
  const [importingVendors, setImportingVendors] = useState(false);

  useEffect(() => {
    loadVendors();
    checkZohoStatus();
  }, [statusFilter, categoryFilter]);

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.id || isAdmin) return;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/users/${user.id}/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserPermissions(response.data.permissions || []);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    };
    fetchPermissions();
  }, [user?.id, isAdmin]);

  const checkZohoStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/zoho/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZohoStatus(response.data);
    } catch (error) {
      console.error('Failed to check Zoho status');
    }
  };

  const handleSyncToZoho = async (vendorId) => {
    setSyncingVendor(vendorId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/vendors/${vendorId}/sync-zoho`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Vendor ${response.data.action} in Zoho Books`);
      loadVendors();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to sync vendor to Zoho';
      toast.error(detail, {
        duration: 15000,
        style: { whiteSpace: 'pre-line', maxWidth: '480px' },
      });
      console.error('[Zoho vendor sync] failed:', detail);
    } finally {
      setSyncingVendor(null);
    }
  };

  // Fetch vendors from Zoho Books
  const fetchZohoVendors = async () => {
    setLoadingZohoVendors(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/zoho/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZohoVendors(response.data.vendors || []);
      setSelectedZohoVendors([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch vendors from Zoho');
    } finally {
      setLoadingZohoVendors(false);
    }
  };

  // Open import dialog and fetch Zoho vendors
  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
    fetchZohoVendors();
  };

  // Toggle vendor selection for import
  const toggleVendorSelection = (contactId) => {
    setSelectedZohoVendors(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      }
      return [...prev, contactId];
    });
  };

  // Select/Deselect all vendors
  const toggleSelectAll = () => {
    if (selectedZohoVendors.length === zohoVendors.length) {
      setSelectedZohoVendors([]);
    } else {
      setSelectedZohoVendors(zohoVendors.map(v => v.contact_id));
    }
  };

  // Import selected vendors from Zoho
  const handleImportVendors = async () => {
    if (selectedZohoVendors.length === 0) {
      toast.error('Please select at least one vendor to import');
      return;
    }
    
    setImportingVendors(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/zoho/vendors/import`,
        selectedZohoVendors,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      // Surface skip reasons inline so the user knows WHY a vendor was
      // skipped (was the recurring "0 imported, 1 skipped — but the
      // vendor isn't in my list" support thread).
      const sd = response.data.skipped_details || [];
      if (sd.length > 0) {
        const top = sd.slice(0, 3).map(
          s => `• ${s.vendor_name}: ${s.reason}`
        ).join('\n');
        const more = sd.length > 3 ? `\n…and ${sd.length - 3} more` : '';
        toast.message('Skipped vendors', { description: top + more, duration: 8000 });
      }
      setImportDialogOpen(false);
      loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import vendors');
    } finally {
      setImportingVendors(false);
    }
  };

  // Import all vendors from Zoho
  const handleImportAllVendors = async () => {
    setImportingVendors(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/zoho/vendors/import`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      const sd = response.data.skipped_details || [];
      if (sd.length > 0) {
        const top = sd.slice(0, 3).map(
          s => `• ${s.vendor_name}: ${s.reason}`
        ).join('\n');
        const more = sd.length > 3 ? `\n…and ${sd.length - 3} more` : '';
        toast.message('Skipped vendors', { description: top + more, duration: 8000 });
      }
      setImportDialogOpen(false);
      loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import vendors');
    } finally {
      setImportingVendors(false);
    }
  };

  const loadVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API_BASE_URL}/enterprise/vendors?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVendors(response.data.vendors || []);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async () => {
    try {
      const token = localStorage.getItem('token');
      // Clean up form data - remove empty/invalid values
      const cleanedData = { ...formData };
      
      // Remove empty strings and null values
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' || cleanedData[key] === null) {
          delete cleanedData[key];
        }
      });
      
      // Fix numeric fields
      if (cleanedData.default_lead_time !== undefined) {
        cleanedData.default_lead_time = parseInt(cleanedData.default_lead_time) || 7;
      }
      if (cleanedData.credit_limit !== undefined) {
        cleanedData.credit_limit = parseFloat(cleanedData.credit_limit) || 0;
      }
      
      // Remove empty bank_details
      if (cleanedData.bank_details && Object.keys(cleanedData.bank_details).length === 0) {
        delete cleanedData.bank_details;
      }
      
      await axios.post(`${API_BASE_URL}/enterprise/vendors`, cleanedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor created successfully');
      setCreateDialogOpen(false);
      setFormData({});
      loadVendors();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Pydantic validation error - extract the first message
        const firstError = errorDetail[0];
        toast.error(firstError?.msg || 'Validation error');
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to create vendor');
      }
    }
  };

  const handleUpdateVendor = async () => {
    try {
      const token = localStorage.getItem('token');
      // Clean up form data
      const cleanedData = { ...formData };
      
      // Fix numeric fields
      if (cleanedData.default_lead_time !== undefined) {
        cleanedData.default_lead_time = parseInt(cleanedData.default_lead_time) || 7;
      }
      if (cleanedData.credit_limit !== undefined) {
        cleanedData.credit_limit = parseFloat(cleanedData.credit_limit) || 0;
      }
      
      await axios.put(`${API_BASE_URL}/enterprise/vendors/${selectedVendor.id}`, cleanedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor updated successfully');
      setEditDialogOpen(false);
      setSelectedVendor(null);
      setFormData({});
      loadVendors();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const firstError = errorDetail[0];
        toast.error(firstError?.msg || 'Validation error');
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to update vendor');
      }
    }
  };

  const handleDeleteVendor = async (vendorId, permanent = false) => {
    const confirmMessage = permanent 
      ? 'Are you sure you want to PERMANENTLY DELETE this vendor? This cannot be undone!'
      : 'Are you sure you want to deactivate this vendor?';
    
    if (!confirm(confirmMessage)) return;
    
    try {
      const token = localStorage.getItem('token');
      const url = permanent 
        ? `${API_BASE_URL}/enterprise/vendors/${vendorId}?permanent=true`
        : `${API_BASE_URL}/enterprise/vendors/${vendorId}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(permanent ? 'Vendor permanently deleted' : 'Vendor deactivated');
      loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || (permanent ? 'Failed to delete vendor' : 'Failed to deactivate vendor'));
    }
  };

  const toggleVendorExpand = (vendorId) => {
    setExpandedVendors(prev => ({
      ...prev,
      [vendorId]: !prev[vendorId]
    }));
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    setFormData(vendor);
    setEditDialogOpen(true);
  };

  const openCreateDialog = () => {
    setFormData({
      vendor_name: '',
      category: '',
      contact_person: '',
      phone: '',
      whatsapp: '',
      email: '',
      address: '',
      city: '',
      country: 'UAE',
      currency: 'AED',
      payment_terms: 'Net 30',
      default_lead_time: 7,
      credit_limit: 0,
      ships_to_warehouse: true,
      pickup_from_vendor: false,
      portal_enabled: false,
      status: 'active'
    });
    setCreateDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'on_hold':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><AlertTriangle className="h-3 w-3 mr-1" />On Hold</Badge>;
      case 'blacklisted':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" />Blacklisted</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">Unknown</Badge>;
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return vendor.vendor_name?.toLowerCase().includes(search) ||
             vendor.vendor_code?.toLowerCase().includes(search) ||
             vendor.contact_person?.toLowerCase().includes(search) ||
             vendor.email?.toLowerCase().includes(search);
    }
    return true;
  });

  // Summary stats
  const activeCount = vendors.filter(v => v.status === 'active').length;
  const totalDue = vendors.reduce((sum, v) => sum + (v.current_due || 0), 0);
  const totalOverdue = vendors.reduce((sum, v) => sum + (v.overdue_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Vendor Master</h1>
          <p className="text-wt-text-muted mt-1">Manage suppliers and vendor relationships</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => { setAgingOpen(true); fetchAging(); }}
            variant="outline"
            className="border-wt-border text-wt-text-secondary hover:text-wt-text-primary"
            data-testid="aging-report-btn"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Aging Report
          </Button>
          {canCreateVendor && (zohoStatus?.configured || zohoStatus?.connected || zohoStatus?.status === 'connected') && (
            <Button 
              onClick={handleOpenImportDialog} 
              variant="outline" 
              className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
              data-testid="import-from-zoho-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Import from Zoho
            </Button>
          )}
          {canCreateVendor && (
            <Button onClick={openCreateDialog} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="add-vendor-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Vendor
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Building2 className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Total Vendors</p>
              <p className="text-2xl font-bold text-wt-text-primary">{vendors.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Active Vendors</p>
              <p className="text-2xl font-bold text-green-400">{activeCount}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <DollarSign className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Total Due</p>
              <p className="text-2xl font-bold text-wt-text-primary">{formatCurrency(totalDue)}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-paper border border-red-500/30 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Overdue</p>
              <p className="text-2xl font-bold text-red-400">{formatCurrency(totalOverdue)}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] bg-wt-bg border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="blacklisted">Blacklisted</SelectItem>
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px] bg-wt-bg border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">
            <SelectItem value="all">All Categories</SelectItem>
            {VENDOR_CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vendors List */}
      <div className="space-y-4">
        {filteredVendors.map((vendor) => {
          const isExpanded = expandedVendors[vendor.id];
          
          return (
            <motion.div
              key={vendor.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden"
            >
              {/* Vendor Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-wt-gold/10 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-wt-gold" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 
                        className="text-lg font-semibold text-wt-text-primary hover:text-wt-gold cursor-pointer"
                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                      >
                        {vendor.vendor_name}
                      </h3>
                      {getStatusBadge(vendor.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-wt-text-muted mt-1 flex-wrap">
                      <span className="text-wt-gold">{vendor.vendor_code}</span>
                      {vendor.category && <span>• {vendor.category}</span>}
                      {vendor.contact_person && <span>• {vendor.contact_person}</span>}
                    </div>
                    {/* Mobile Quick Action */}
                    <div className="md:hidden mt-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                        className="bg-wt-gold hover:bg-wt-gold-hover text-black text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View Details & Ledger
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="text-right mr-4">
                    <p className="text-sm text-wt-text-muted">Due Amount</p>
                    <p className={`font-semibold ${vendor.current_due > 0 ? 'text-orange-400' : 'text-wt-text-primary'}`}>
                      {formatCurrency(vendor.current_due || 0)}
                    </p>
                  </div>
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleVendorExpand(vendor.id)}
                    className="text-wt-text-muted"
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/vendors/${vendor.id}`)}
                    className="text-wt-text-muted hover:text-blue-400"
                    title="View Performance & Ledger"
                    data-testid={`view-vendor-${vendor.id}`}
                  >
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                  {/* Zoho Sync Button */}
                  {zohoStatus?.configured && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSyncToZoho(vendor.id)}
                      disabled={syncingVendor === vendor.id}
                      className={`text-wt-text-muted hover:text-purple-400 ${vendor.zoho_vendor_id ? 'text-purple-400' : ''}`}
                      title={vendor.zoho_vendor_id ? 'Update in Zoho' : 'Sync to Zoho'}
                      data-testid={`zoho-sync-vendor-${vendor.id}`}
                    >
                      {syncingVendor === vendor.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CloudUpload className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(vendor)}
                    className="text-wt-text-muted hover:text-wt-gold"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-wt-text-muted hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-wt-surface border-wt-border">
                      <DropdownMenuItem
                        onClick={() => handleDeleteVendor(vendor.id, false)}
                        className="text-wt-text-primary hover:bg-wt-paper cursor-pointer"
                      >
                        <XCircle className="h-4 w-4 mr-2 text-yellow-500" />
                        Deactivate Vendor
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-wt-border" />
                      <DropdownMenuItem
                        onClick={() => handleDeleteVendor(vendor.id, true)}
                        className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Permanently Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-wt-border p-4 bg-wt-subtle/30">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Contact Info */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                        <Users className="h-4 w-4 text-wt-gold" />
                        Contact Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        {vendor.phone && (
                          <div className="flex items-center gap-2 text-wt-text-muted">
                            <Phone className="h-4 w-4" />
                            <a href={`tel:${vendor.phone}`} className="hover:text-wt-gold">{vendor.phone}</a>
                          </div>
                        )}
                        {vendor.whatsapp && (
                          <div className="flex items-center gap-2 text-wt-text-muted">
                            <ExternalLink className="h-4 w-4" />
                            <a href={`https://wa.me/${vendor.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-green-400">WhatsApp</a>
                          </div>
                        )}
                        {vendor.email && (
                          <div className="flex items-center gap-2 text-wt-text-muted">
                            <Mail className="h-4 w-4" />
                            <a href={`mailto:${vendor.email}`} className="hover:text-wt-gold">{vendor.email}</a>
                          </div>
                        )}
                        {vendor.address && (
                          <div className="flex items-start gap-2 text-wt-text-muted">
                            <MapPin className="h-4 w-4 mt-0.5" />
                            <span>{vendor.address}{vendor.city ? `, ${vendor.city}` : ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Commercial Terms */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-wt-gold" />
                        Commercial Terms
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">Payment Terms:</span>
                          <span className="text-wt-text-primary">{vendor.payment_terms || 'Net 30'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">Lead Time:</span>
                          <span className="text-wt-text-primary">{vendor.default_lead_time || 7} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">Credit Limit:</span>
                          <span className="text-wt-text-primary">{formatCurrency(vendor.credit_limit || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">Currency:</span>
                          <span className="text-wt-text-primary">{vendor.currency || 'AED'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Logistics & Performance */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                        <Truck className="h-4 w-4 text-wt-gold" />
                        Logistics & Performance
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">Delivery Method:</span>
                          <span className="text-wt-text-primary">
                            {vendor.ships_to_warehouse ? 'Ships to Warehouse' : ''}
                            {vendor.pickup_from_vendor ? 'Pickup Required' : ''}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">Total POs:</span>
                          <span className="text-wt-text-primary">{vendor.total_po_count || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">On-Time %:</span>
                          <span className="text-green-400">{vendor.on_time_percentage || 100}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-wt-text-muted">Portal Access:</span>
                          <span className={vendor.portal_enabled ? 'text-green-400' : 'text-wt-text-muted'}>
                            {vendor.portal_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 pt-4 border-t border-wt-border flex flex-wrap gap-2">
                    <Button
                      onClick={() => navigate(`/vendors/${vendor.id}`)}
                      className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                      data-testid={`view-details-btn-${vendor.id}`}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details & Ledger
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => openEditDialog(vendor)}
                      className="border-wt-border text-wt-text-primary"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
        
        {filteredVendors.length === 0 && (
          <div className="text-center py-12 bg-wt-paper border border-wt-border rounded-xl">
            <Building2 className="h-12 w-12 text-wt-text-muted mx-auto mb-4" />
            <p className="text-wt-text-muted">No vendors found</p>
            <Button onClick={openCreateDialog} className="mt-4 bg-wt-gold hover:bg-wt-gold-hover text-black">
              Add Your First Vendor
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Vendor Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={() => {
        setCreateDialogOpen(false);
        setEditDialogOpen(false);
        setSelectedVendor(null);
        setFormData({});
      }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              {editDialogOpen ? 'Edit Vendor' : 'Add New Vendor'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="bg-wt-subtle w-full grid grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="commercial">Commercial</TabsTrigger>
              <TabsTrigger value="logistics">Logistics</TabsTrigger>
              <TabsTrigger value="portal">Portal</TabsTrigger>
            </TabsList>
            
            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-wt-text-primary">Vendor Name *</Label>
                  <Input
                    value={formData.vendor_name || ''}
                    onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    placeholder="Enter vendor name"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Category</Label>
                  <Select 
                    value={formData.category || ''} 
                    onValueChange={(v) => setFormData({...formData, category: v})}
                  >
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      {VENDOR_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Contact Person</Label>
                  <Input
                    value={formData.contact_person || ''}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Phone</Label>
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">WhatsApp</Label>
                  <Input
                    value={formData.whatsapp || ''}
                    onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-wt-text-primary">Email</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-wt-text-primary">Address</Label>
                  <Textarea
                    value={formData.address || ''}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">City</Label>
                  <Input
                    value={formData.city || ''}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Country</Label>
                  <Input
                    value={formData.country || 'UAE'}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">TRN/GST Number</Label>
                  <Input
                    value={formData.trn_gst || ''}
                    onChange={(e) => setFormData({...formData, trn_gst: e.target.value})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Status</Label>
                  <Select 
                    value={formData.status || 'active'} 
                    onValueChange={(v) => setFormData({...formData, status: v})}
                  >
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="blacklisted">Blacklisted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            
            {/* Commercial Tab */}
            <TabsContent value="commercial" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-wt-text-primary">Payment Terms</Label>
                  <Select 
                    value={formData.payment_terms || 'Net 30'} 
                    onValueChange={(v) => setFormData({...formData, payment_terms: v})}
                  >
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      {PAYMENT_TERMS.map(term => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Default Lead Time (days)</Label>
                  <Input
                    type="number"
                    value={formData.default_lead_time || 7}
                    onChange={(e) => setFormData({...formData, default_lead_time: parseInt(e.target.value)})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Credit Limit</Label>
                  <Input
                    type="number"
                    value={formData.credit_limit || 0}
                    onChange={(e) => setFormData({...formData, credit_limit: parseFloat(e.target.value)})}
                    className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Currency</Label>
                  <Select 
                    value={formData.currency || 'AED'} 
                    onValueChange={(v) => setFormData({...formData, currency: v})}
                  >
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      <SelectItem value="AED">AED</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="INR">INR</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="border-t border-wt-border pt-4">
                <h4 className="text-sm font-medium text-wt-text-primary mb-4">Bank Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-wt-text-primary">Bank Name</Label>
                    <Input
                      value={formData.bank_details?.bank_name || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        bank_details: {...(formData.bank_details || {}), bank_name: e.target.value}
                      })}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-primary">Account Name</Label>
                    <Input
                      value={formData.bank_details?.account_name || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        bank_details: {...(formData.bank_details || {}), account_name: e.target.value}
                      })}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-primary">Account Number</Label>
                    <Input
                      value={formData.bank_details?.account_number || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        bank_details: {...(formData.bank_details || {}), account_number: e.target.value}
                      })}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-primary">IBAN</Label>
                    <Input
                      value={formData.bank_details?.iban || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        bank_details: {...(formData.bank_details || {}), iban: e.target.value}
                      })}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Logistics Tab */}
            <TabsContent value="logistics" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-wt-subtle rounded-lg">
                  <div>
                    <p className="text-wt-text-primary font-medium">Ships to Warehouse</p>
                    <p className="text-sm text-wt-text-muted">Vendor delivers products to our warehouse</p>
                  </div>
                  <Switch
                    checked={formData.ships_to_warehouse !== false}
                    onCheckedChange={(checked) => setFormData({...formData, ships_to_warehouse: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-wt-subtle rounded-lg">
                  <div>
                    <p className="text-wt-text-primary font-medium">Pickup from Vendor</p>
                    <p className="text-sm text-wt-text-muted">We pickup products from vendor's location</p>
                  </div>
                  <Switch
                    checked={formData.pickup_from_vendor === true}
                    onCheckedChange={(checked) => setFormData({...formData, pickup_from_vendor: checked})}
                  />
                </div>
                
                {formData.pickup_from_vendor && (
                  <div>
                    <Label className="text-wt-text-primary">Vendor Warehouse Address</Label>
                    <Textarea
                      value={formData.vendor_warehouse_address || ''}
                      onChange={(e) => setFormData({...formData, vendor_warehouse_address: e.target.value})}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                      rows={3}
                      placeholder="Enter pickup location address"
                    />
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Portal Tab */}
            <TabsContent value="portal" className="space-y-4 mt-4">
              <div className="flex items-center justify-between p-4 bg-wt-subtle rounded-lg">
                <div>
                  <p className="text-wt-text-primary font-medium">Enable Vendor Portal</p>
                  <p className="text-sm text-wt-text-muted">Allow vendor to login and view their POs</p>
                </div>
                <Switch
                  checked={formData.portal_enabled === true}
                  onCheckedChange={(checked) => setFormData({...formData, portal_enabled: checked})}
                />
              </div>
              
              {formData.portal_enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-wt-text-primary">Portal Username</Label>
                    <Input
                      value={formData.portal_username || ''}
                      onChange={(e) => setFormData({...formData, portal_username: e.target.value})}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-primary">Portal Password</Label>
                    <Input
                      type="password"
                      placeholder="Set new password"
                      onChange={(e) => setFormData({...formData, portal_password: e.target.value})}
                      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                    />
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-wt-border mt-4">
            <Button 
              variant="ghost" 
              onClick={() => {
                setCreateDialogOpen(false);
                setEditDialogOpen(false);
              }}
              className="text-wt-text-secondary"
            >
              Cancel
            </Button>
            <Button 
              onClick={editDialogOpen ? handleUpdateVendor : handleCreateVendor}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              {editDialogOpen ? 'Update Vendor' : 'Create Vendor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payables Aging Report Dialog */}
      <Dialog open={agingOpen} onOpenChange={setAgingOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-wt-gold" />
              Vendor Payables Aging
            </DialogTitle>
          </DialogHeader>
          {agingLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-wt-gold" /></div>
          ) : !agingData || agingData.rows.length === 0 ? (
            <p className="text-wt-text-muted text-sm py-6 text-center" data-testid="aging-empty">No outstanding vendor payables. 🎉</p>
          ) : (
            <div className="overflow-x-auto" data-testid="aging-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-wt-text-muted text-xs border-b border-wt-border">
                    <th className="text-left py-2 pr-2">Vendor</th>
                    <th className="text-right py-2 px-2">Not yet due</th>
                    <th className="text-right py-2 px-2">1-30d</th>
                    <th className="text-right py-2 px-2">31-60d</th>
                    <th className="text-right py-2 px-2">61-90d</th>
                    <th className="text-right py-2 px-2">90d+</th>
                    <th className="text-right py-2 px-2">Overdue</th>
                    <th className="text-right py-2 pl-2">Total Due</th>
                  </tr>
                </thead>
                <tbody>
                  {agingData.rows.map((r) => (
                    <tr key={r.vendor_id} className="border-b border-wt-border/50" data-testid={`aging-row-${r.vendor_id}`}>
                      <td className="py-2 pr-2">
                        <p className="text-wt-text-primary font-medium">{r.vendor_name}</p>
                        <p className="text-xs text-wt-text-muted">{r.payment_terms || 'Net 30'}</p>
                      </td>
                      <td className="text-right px-2 text-wt-text-secondary">{formatCurrency(r.aging?.current || 0)}</td>
                      <td className="text-right px-2 text-amber-400">{formatCurrency(r.aging?.d1_30 || 0)}</td>
                      <td className="text-right px-2 text-orange-400">{formatCurrency(r.aging?.d31_60 || 0)}</td>
                      <td className="text-right px-2 text-red-400">{formatCurrency(r.aging?.d61_90 || 0)}</td>
                      <td className="text-right px-2 text-red-500 font-semibold">{formatCurrency(r.aging?.d90_plus || 0)}</td>
                      <td className="text-right px-2 text-red-400 font-semibold">{formatCurrency(r.overdue || 0)}</td>
                      <td className="text-right pl-2 text-wt-text-primary font-semibold">{formatCurrency(r.current_due || 0)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold">
                    <td className="py-2 pr-2 text-wt-text-primary">Total</td>
                    <td className="text-right px-2 text-wt-text-secondary">{formatCurrency(agingData.totals?.aging?.current || 0)}</td>
                    <td className="text-right px-2 text-amber-400">{formatCurrency(agingData.totals?.aging?.d1_30 || 0)}</td>
                    <td className="text-right px-2 text-orange-400">{formatCurrency(agingData.totals?.aging?.d31_60 || 0)}</td>
                    <td className="text-right px-2 text-red-400">{formatCurrency(agingData.totals?.aging?.d61_90 || 0)}</td>
                    <td className="text-right px-2 text-red-500">{formatCurrency(agingData.totals?.aging?.d90_plus || 0)}</td>
                    <td className="text-right px-2 text-red-400">{formatCurrency(agingData.totals?.overdue || 0)}</td>
                    <td className="text-right pl-2 text-wt-gold">{formatCurrency(agingData.totals?.current_due || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import from Zoho Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-xl flex items-center gap-3">
              <Download className="h-5 w-5 text-wt-gold" />
              Import Vendors from Zoho Books
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Actions Bar */}
            <div className="flex items-center justify-between py-3 border-b border-wt-border">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchZohoVendors}
                  disabled={loadingZohoVendors}
                  className="border-wt-border"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingZohoVendors ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <span className="text-sm text-wt-text-muted">
                  {zohoVendors.length} vendors found in Zoho
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedZohoVendors.length === zohoVendors.length && zohoVendors.length > 0}
                  onCheckedChange={toggleSelectAll}
                  id="select-all"
                />
                <Label htmlFor="select-all" className="text-sm text-wt-text-secondary cursor-pointer">
                  Select All
                </Label>
              </div>
            </div>
            
            {/* Vendor List */}
            <div className="flex-1 overflow-y-auto py-3">
              {loadingZohoVendors ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
                  <span className="ml-3 text-wt-text-muted">Fetching vendors from Zoho...</span>
                </div>
              ) : zohoVendors.length === 0 ? (
                <div className="text-center py-12 text-wt-text-muted">
                  No vendors found in Zoho Books
                </div>
              ) : (
                <div className="space-y-2">
                  {zohoVendors.map(vendor => {
                    const isSelected = selectedZohoVendors.includes(vendor.contact_id);
                    const existingVendor = vendors.find(v => 
                      v.zoho_vendor_id === vendor.contact_id || 
                      v.vendor_name?.toLowerCase() === vendor.contact_name?.toLowerCase()
                    );
                    
                    return (
                      <div
                        key={vendor.contact_id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          isSelected 
                            ? 'bg-wt-gold/10 border-wt-gold' 
                            : existingVendor 
                              ? 'bg-wt-surface/50 border-wt-border opacity-60' 
                              : 'bg-wt-surface border-wt-border hover:border-wt-gold/50'
                        }`}
                        onClick={() => !existingVendor && toggleVendorSelection(vendor.contact_id)}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={!!existingVendor}
                          onCheckedChange={() => toggleVendorSelection(vendor.contact_id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-wt-text-primary">
                              {vendor.contact_name}
                            </span>
                            {existingVendor && (
                              <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                                Already Exists
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-wt-text-muted">
                            {vendor.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {vendor.email}
                              </span>
                            )}
                            {vendor.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {vendor.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <span className="text-wt-text-muted">{vendor.currency_code || 'AED'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t border-wt-border pt-4 flex justify-between">
            <div className="text-sm text-wt-text-muted">
              {selectedZohoVendors.length} vendor(s) selected
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setImportDialogOpen(false)}
                className="border-wt-border"
              >
                Cancel
              </Button>
              <Button
                onClick={handleImportAllVendors}
                disabled={importingVendors || zohoVendors.length === 0}
                variant="outline"
                className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
              >
                {importingVendors ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Import All
              </Button>
              <Button
                onClick={handleImportVendors}
                disabled={importingVendors || selectedZohoVendors.length === 0}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {importingVendors ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Import Selected ({selectedZohoVendors.length})
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorMasterPage;
